import { useCallback, useEffect, useRef } from 'react';

import { toggleMuteAction } from '../../../pages/call/features/device';
import { getParticipantById, selectDevice } from '../../../pages/call/selectors';
import { SELF } from '../../constants';
import { compareKeyCode, KeyCodes } from '../../helpers/keyboard-keys';
import { useActions } from '../useActions';
import { useTypedSelector } from '../useTypedSelector';

const UNMUTE_BY_SPACE_DELAY = 50;

const AUDIO_TRACK_TYPE = 'audio';

const SKIP_ELEMENTS = ['input', 'textarea', 'button'];

const MAX_SPACE_CLICK_TIME = 500;

export function useSpaceUnmute(isActive = true) {
    const { toggleMute } = useActions({
        toggleMute: toggleMuteAction,
    });
    const audioIsTemporarilyUnMuted = useTypedSelector((state) => selectDevice(state).audioIsTemporarilyUnMuted);
    const participant = useTypedSelector((state) => getParticipantById(state, SELF));
    const unmuteTimeout = useRef<ReturnType<typeof setTimeout>>();
    const spaceDownTime = useRef<number>(0);

    const clearUnmuteTimeout = () => {
        if (unmuteTimeout.current) {
            clearTimeout(unmuteTimeout.current);
        }
    };

    const onSpaceDown = useCallback(() => {
        spaceDownTime.current = Date.now();

        clearUnmuteTimeout();
        if (participant?.audio?.muted) {
            unmuteTimeout.current = setTimeout(() => {
                toggleMute({ type: AUDIO_TRACK_TYPE, value: false, isTemporarily: true });
            }, UNMUTE_BY_SPACE_DELAY);
        }
    }, [participant?.audio?.muted, toggleMute]);

    const onSpaceUp = useCallback(() => {
        clearUnmuteTimeout();

        const clickTime = Date.now() - spaceDownTime.current;
        const isClickHappened = clickTime <= MAX_SPACE_CLICK_TIME;

        if (isClickHappened && audioIsTemporarilyUnMuted) {
            toggleMute({ type: AUDIO_TRACK_TYPE, value: false, isTemporarily: false });

            return;
        }

        if (isClickHappened) {
            toggleMute({ type: AUDIO_TRACK_TYPE, value: !participant?.audio?.muted, isTemporarily: false });

            return;
        }

        if (audioIsTemporarilyUnMuted) {
            toggleMute({ type: AUDIO_TRACK_TYPE, value: true, isTemporarily: true });
        }
    }, [audioIsTemporarilyUnMuted, participant?.audio?.muted, toggleMute]);

    const onKeydown = useCallback(
        (event: KeyboardEvent) => {
            const elementTag = (event.target as Element)?.tagName.toLowerCase();

            if (event.repeat || SKIP_ELEMENTS.includes(elementTag)) {
                return;
            }

            if (compareKeyCode(event.code, KeyCodes.SPACE)) {
                onSpaceDown();
            }
        },
        [onSpaceDown]
    );

    const onKeyup = useCallback(
        (event: KeyboardEvent) => {
            if (compareKeyCode(event.code, KeyCodes.SPACE)) {
                onSpaceUp();
            }
        },
        [onSpaceUp]
    );

    useEffect(() => {
        if (isActive) {
            document.addEventListener('keydown', onKeydown);
            document.addEventListener('keyup', onKeyup);
            return () => {
                clearUnmuteTimeout();
                document.removeEventListener('keydown', onKeydown);
                document.removeEventListener('keyup', onKeyup);
            };
        }
    }, [onKeydown, onKeyup, isActive]);
}
